/**
 * Catalog core — loads pathogen records, validates, indexes, hashes.
 *
 * Environment-agnostic: does not touch the filesystem. Raw JSON records
 * arrive through a registered catalog source. Node (`loader.ts`) registers
 * an fs-scanning source on import; browser callers can use
 * `__setPathogenRecords` directly.
 */

import type { Pathogen, RawPathogenRecord } from './types.js';
import { validatePathogen } from './validate.js';
import { sha256Hex } from './sha256.js';

export type CatalogSource = () => RawPathogenRecord[];

interface CachedEntry {
  pathogen: Pathogen;
  sourcePath: string;
}

interface LoadFailure {
  sourcePath: string;
  errors: string[];
}

let cacheById: Map<string, CachedEntry> | null = null;
let cacheByAlias: Map<string, CachedEntry> | null = null;
let loadFailures: LoadFailure[] = [];
let warnedAboutFailures = false;
let catalogHashHex: string | null = null;

let catalogSource: CatalogSource | null = null;
let dataDirectoryOverride: string | null = null;

export function registerCatalogSource(source: CatalogSource): void {
  catalogSource = source;
  reload();
}

/** Inject pre-loaded raw records (browser / worker / test use). */
export function __setPathogenRecords(records: RawPathogenRecord[]): void {
  const frozen = records.slice();
  catalogSource = () => frozen;
  reload();
}

/** Test/Node hook to redirect the loader at a different directory. */
export function __setDataDirectory(path: string | null): void {
  dataDirectoryOverride = path;
  reload();
}

export function getDataDirectoryOverride(): string | null {
  return dataDirectoryOverride;
}

export function reload(): void {
  cacheById = null;
  cacheByAlias = null;
  loadFailures = [];
  warnedAboutFailures = false;
  catalogHashHex = null;
}

export function getLoadFailures(): readonly LoadFailure[] {
  ensureLoaded();
  return loadFailures;
}

export function getCatalogHash(): string {
  ensureLoaded();
  return catalogHashHex ?? '';
}

function ensureLoaded(): void {
  if (cacheById !== null) return;
  cacheById = new Map();
  cacheByAlias = new Map();
  loadFailures = [];
  catalogHashHex = null;

  if (!catalogSource) {
    return;
  }
  const records = catalogSource();
  // Sort by sourcePath for deterministic hash.
  records.sort((a, b) => a.sourcePath.localeCompare(b.sourcePath));

  const hashParts: string[] = [];
  for (const raw of records) {
    hashParts.push(raw.sourcePath, '\n', raw.raw, '\n');
    const result = validatePathogen(raw.raw, raw.sourcePath);
    if (!result.valid) {
      loadFailures.push({ sourcePath: raw.sourcePath, errors: result.errors });
      continue;
    }
    const p = result.pathogen;
    if (cacheById.has(p.id)) {
      loadFailures.push({
        sourcePath: raw.sourcePath,
        errors: [`duplicate pathogen id '${p.id}'`],
      });
      continue;
    }
    cacheById.set(p.id, { pathogen: p, sourcePath: raw.sourcePath });
    indexAlias(p.names.common.toLowerCase(), p, raw.sourcePath);
    indexAlias(p.names.scientific.toLowerCase(), p, raw.sourcePath);
    for (const a of p.names.aliases ?? []) {
      indexAlias(a.toLowerCase(), p, raw.sourcePath);
    }
  }
  catalogHashHex = sha256Hex(hashParts.join(''));

  if (loadFailures.length > 0 && !warnedAboutFailures) {
    warnedAboutFailures = true;
    const summary = loadFailures
      .map(f => `  ${f.sourcePath}: ${f.errors.length} error(s)`)
      .join('\n');
    console.warn(
      `[@biomax/plugin-pathogen-database] ${loadFailures.length} record(s) failed to load:\n${summary}`,
    );
  }
}

function indexAlias(alias: string, p: Pathogen, sourcePath: string): void {
  if (!cacheByAlias) return;
  // Only index aliases that don't collide with another pathogen.
  // First-wins for stability; this matches drug-database behaviour.
  if (!cacheByAlias.has(alias)) {
    cacheByAlias.set(alias, { pathogen: p, sourcePath });
  }
}

export function loaderFindPathogen(idOrAlias: string): Pathogen | undefined {
  ensureLoaded();
  const direct = cacheById!.get(idOrAlias);
  if (direct) return direct.pathogen;
  const alias = cacheByAlias!.get(idOrAlias.toLowerCase());
  return alias?.pathogen;
}

export function loaderListPathogens(): Pathogen[] {
  ensureLoaded();
  return Array.from(cacheById!.values()).map(e => e.pathogen);
}
